/**
 * Значення поля-зв'язку на екрані (bec368f0): назва цілі з `refs` відповіді — посиланням на її
 * картку, без доступу — «Запис недоступний», без елемента — «Інший запис», обидва без посилання
 * (`lookup-refs.ts`). Індекс назв дає екран (список — з усіх порцій, картка — зі своєї відповіді)
 * через `RefsProvider`. Тут — те, що бере перший екран (клітинка списку); поле-зв'язок картки
 * (`LookupLink`) — у `card/LookupLink.tsx`, у лінивому чанку картки.
 */
import type { FieldValue, MetaField } from '@clientline/contracts';
import { useNavigate } from '@tanstack/react-router';
import { createContext, type MouseEvent, type ReactNode, useContext } from 'react';
import { EMPTY_REFS, lookupShown, type RefIndex } from './lookup-refs.ts';

/** Адреса картки — як `cardHref` у list-source (без імпорту звідти: цикл модулів). */
const cardHref = (entity: string, id: string) =>
  `/r/${encodeURIComponent(entity)}/${encodeURIComponent(id)}`;

const RefsContext = createContext<RefIndex>(EMPTY_REFS);

export function RefsProvider({ refs, children }: { refs: RefIndex; children: ReactNode }) {
  return <RefsContext.Provider value={refs}>{children}</RefsContext.Provider>;
}

export const useRefs = () => useContext(RefsContext);

/** Ціль без посилання: `hidden` — без доступу, `unknown` — елемента в `refs` немає. */
export const Unavailable = ({ kind, text }: { kind: 'hidden' | 'unknown'; text: string }) => (
  <span className="muted" data-lookup={kind}>
    {text}
  </span>
);

const modified = (e: MouseEvent) =>
  e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

/**
 * Поле-зв'язок у клітинці списку: звичайне `<a>`, як назва рядка (RecordTable: `<Link>` у кожному
 * рядку дорогий для першого рендера), поза порядком Tab — рядок відкривають клавішами, а з картки
 * ціль досяжна посиланням. Звичайний клік — перехід без перезавантаження, з модифікатором —
 * браузером (нова вкладка).
 */
export function LookupCell({
  field,
  value,
}: {
  field: MetaField & { target: string };
  value: FieldValue | undefined;
}) {
  // `useNavigate` стану роутера не підписує (на відміну від `<Link>`), тож рядки не перемальовує.
  const navigate = useNavigate();
  const shown = lookupShown(field, value, useRefs());
  if (!shown) return null;
  if (shown.kind !== 'link') return <Unavailable kind={shown.kind} text={shown.text} />;
  return (
    <a
      href={cardHref(shown.entity, shown.id)}
      tabIndex={-1}
      className="truncate"
      title={shown.text}
      data-lookup="link"
      onClick={(e) => {
        if (modified(e)) return;
        e.preventDefault();
        void navigate({
          to: '/r/$entity/$recordId',
          params: { entity: shown.entity, recordId: shown.id },
        });
      }}
    >
      {shown.text}
    </a>
  );
}
